/**
 * useGameWithMap — composite hook that joins a game's live state with its
 * static map geometry.
 *
 * After the Tier 3 perf change the wire `GameState` carries only
 * `state.mapId: string` — the full MapDef (80–284 KB) is no longer embedded
 * in every poll response. Instead, `useMap(mapId)` fetches it once with
 * `staleTime: Infinity`, and this hook wires the two together.
 *
 * Returns:
 *   game          — the raw GameResponse (state.map is absent on the wire)
 *   hydratedState — GameState with `map: MapDef` re-populated from the
 *                   separate map cache. Null while either query is loading or
 *                   when the game has no active state (LOBBY).
 *   mapDef        — the full MapDef for direct consumer access
 *   isLoading     — true while EITHER the game or the map is loading
 *   isMapLoading  — true while only the map query is in-flight
 *   error         — first error from either query
 *   refetch       — re-fetches the game query (map is cached forever)
 *
 * Usage:
 *   const { game, hydratedState, mapDef, isLoading } = useGameWithMap(id)
 *
 * Existing consumers that call `getGameState(game)?.map` should migrate to
 * `hydratedState?.map` so they benefit from the decoupled cache.
 */

import { useEffect, useMemo, useRef } from 'react'
import { useGame } from './useGames'
import { prefetchMap, useMap } from './useMaps'
import { getGameState } from '../lib/gameState'
import type { HydratedGameState } from '../lib/gameState'
import { createHydrateCache, hydrateMap } from '../lib/hydrateTerritories'

export function useGameWithMap(
  id: string | undefined,
  opts?: {
    /**
     * Forward the WebSocket `connected` boolean so `useGame`'s poll can be
     * suppressed while the socket is alive. See `useGame` for the full
     * rationale.
     */
    wsConnected?: boolean
  },
) {
  const {
    data: game,
    isLoading: isGameLoading,
    isRefetching,
    error: gameError,
    refetch,
  } = useGame(id, { wsConnected: opts?.wsConnected })

  // The mapId to fetch: prefer the top-level game.mapId column (always set
  // for games started after mapId was locked at creation time). Fall back to
  // the mapId embedded in the game state JSON (same value, but available as
  // a belt-and-suspenders for legacy rows).
  const mapId = useMemo(() => {
    if (game?.mapId) return game.mapId
    const wireState = game ? getGameState(game) : null
    return wireState?.mapId ?? undefined
  }, [game])

  // Lobby screens do not need geometry to render, but the selected map is the
  // most likely next screen dependency. Warm it in the shared React Query
  // cache while the player is waiting; when the game starts, useMap enables
  // the same query and adopts the result without another request.
  const mapNeeded = game?.status !== 'LOBBY'
  useEffect(() => {
    if (game?.status === 'LOBBY') prefetchMap(mapId)
  }, [game?.status, mapId])

  const {
    data: mapDef,
    isLoading: isMapLoading,
    error: mapError,
  } = useMap(mapId, { enabled: mapNeeded })

  // Structural sharing across game-cache writes lives in the pure helper — see
  // lib/hydrateTerritories.ts for the measurements that motivate it.
  const shared = useRef(createHydrateCache())

  const hydratedState = useMemo<HydratedGameState | null>(() => {
    if (!game) return null
    const wireState = getGameState(game)
    if (!wireState) return null
    if (!mapDef) return null
    // Re-hydrate: inject the MapDef into the wire state. The `map.territories`
    // on the MapDef carry only static geometry (shape, position, adjacencies,
    // regionId). Mutable fields (ownerId, armies) live on the wire state's
    // territory list — we merge them so MapView and the phase UIs see the
    // combined shape.
    // wireState.territories carries the live {id, ownerId, armies} per territory.
    // Merge it with the static geometry from mapDef to produce full Territory objects.
    const map = hydrateMap(shared.current, mapDef, wireState.territories)
    return { ...wireState, map }
  }, [game, mapDef])

  return {
    game,
    hydratedState,
    mapDef,
    isLoading: isGameLoading || (mapNeeded && !!mapId && isMapLoading),
    isGameLoading,
    isMapLoading,
    isRefetching,
    error: gameError ?? mapError ?? null,
    refetch,
  }
}
